import { createApp } from 'vue';
import * as MaiaUiPlusIconsVue from '@maia-ui-plus/icons-vue';

import 'element-plus/theme-chalk/src/message.scss'; // 单独引入message样式
import 'element-plus/theme-chalk/src/message-box.scss'; // 单独引入message-box样式
import 'element-plus/theme-chalk/src/notification.scss'; // 单独引入notification样式
import '@/styles/index.scss'; // 自定义element-plus主题
import 'element-plus/theme-chalk/src/badge.scss'; // 单独引入badge样式

import components from '@/components/index';
// eslint-disable-next-line import/no-unresolved
import 'virtual:svg-icons-register'; // svg注册器
import directives from '@/directives'; // 自定义全局指令

import {
  registerMicroApps, addGlobalUncaughtErrorHandler,
} from 'qiankun';
import config from '@/config/index';

import App from '@/App.vue';
import router from '@/router/index';
import { setupStore } from '@/store/index';
import { useOrgStore } from '@/store/org';
import { actions } from '@/hooks/index';

import AgiWebChat from 'agi-chat'; // 引入插件
import 'agi-chat/dist/index.css'; // 引入css
import { LocalStorageService } from '@/utils/storage';
import { CurrentTenantInfo, ExpandInfo } from '@/api/modules/tenant';
import { crossTabCommunication } from '@/utils/cross-tab';
import { useTenantStore } from '@/store/tenant';

const { VITE_APP_NODE_ENV } = import.meta.env;

/**
 * @description: 从 localStorage 获取当前租户的 AgiWebChat 配置
 * @returns AgiWebChat 配置信息，如果未配置则返回 null
 */
const getAgiWebChatConfig = (): { digitalStaffId: string; channel: string } | null => {
  const currentTenant = LocalStorageService.get('_current_tenant') as CurrentTenantInfo | null;
  if (!currentTenant?.expandInfo) {
    return null;
  }

  try {
    let expandInfo: ExpandInfo | string = currentTenant.expandInfo;
    if (typeof expandInfo === 'string') {
      expandInfo = JSON.parse(expandInfo) as ExpandInfo;
    }

    const { digitalStaffId, agiChatChannel } = expandInfo;
    // 只有当两个字段都有值时才启用 AgiWebChat
    if (digitalStaffId && agiChatChannel) {
      return { digitalStaffId, channel: agiChatChannel };
    }
  } catch (error) {
    console.error('解析 expandInfo 失败:', error);
  }

  return null;
};

const agiChatConfig = getAgiWebChatConfig();

/**
 * @description: 根据租户配置动态设置 favicon
 */
const setFavicon = () => {
  const currentTenant = LocalStorageService.get('_current_tenant') as CurrentTenantInfo | null;
  if (!currentTenant?.expandInfo) {
    return;
  }

  try {
    let expandInfo: ExpandInfo | string = currentTenant.expandInfo;
    if (typeof expandInfo === 'string') {
      expandInfo = JSON.parse(expandInfo) as ExpandInfo;
    }

    const tenantLogo = (expandInfo as ExpandInfo)?.tenantLogo;
    if (tenantLogo) {
      // 查找现有的 favicon link 元素
      let faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!faviconLink) {
        // 如果不存在则创建
        faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        faviconLink.type = 'image/png';
        document.head.appendChild(faviconLink);
      }
      faviconLink.href = tenantLogo;
    }
  } catch (error) {
    console.error('设置 favicon 失败:', error);
  }
};

// 初始化时设置 favicon
setFavicon();

// app
const mainApp = createApp(App);
setupStore(mainApp);

// 注册导航图标
for (const [key, component] of Object.entries(MaiaUiPlusIconsVue)) {
  mainApp.component(key, component);
}

// 根据租户配置动态注册 AgiWebChat 插件
if (agiChatConfig) {
  const envMap: TypeObject = {
    uat: 'UAT',
    production: 'PROD',
  };
  mainApp.use(AgiWebChat, {
    env: envMap[VITE_APP_NODE_ENV] || 'DEV',
    channel: agiChatConfig.channel,
  });
}

mainApp
  .use(router)
  .use(components)
  .use(directives)
  .mount('#mainApp');

// 注册微应用
registerMicroApps(config.microApps, {
  beforeLoad: (app: any): any => {
    console.log('before load app.name====>>>>>', app.name);
  },
  beforeMount: [
    (app: any): any => {
      console.log('[LifeCycle] before mount %c%s', 'color: green;', app.name);
      actions.setGlobalState({
        currentOrgInfo: {},
        currentOrgInfoList: [],
        currentOrgIds: [],
      });
    }
  ],
  afterMount: [
    (app: any): any => {
      console.log('[LifeCycle] after mount %c%s', 'color: green;', app.name);
      const orgStore = useOrgStore();
      actions.setGlobalState({
        currentOrgInfo: orgStore.currentOrgInfo,
        currentOrgInfoList: orgStore.currentOrgInfoList,
        currentOrgIds: orgStore.currentOrgIds,
        type: 'init',
      });
    }
  ],
  afterUnmount: [
    (app: any): any => {
      console.log('[LifeCycle] after unmount %c%s', 'color: green;', app.name);
    }
  ],
});

// 添加全局异常捕获
addGlobalUncaughtErrorHandler((handler: any) => {
  console.log('主应用-异常捕获', handler);
});

/**
 * @description: 初始化跨页签通信监听
 * 当其他页签切换租户时，当前页签也会刷新以同步租户状态
 */
const setupCrossTabListener = () => {
  crossTabCommunication.init((message) => {
    if (message.type === 'TENANT_SWITCH') {
      const tenantStore = useTenantStore();

      // 检查是否需要切换（避免自己发出的消息再次触发）
      if (message.payload?.tenantId && message.payload.tenantId !== tenantStore.currentTenantId) {
        const tenantDomain = message.payload.tenantDomain;
        if (tenantDomain) {
          // 有配置域名，跳转到目标租户对应的域名
          const url = tenantDomain.startsWith('http') ? tenantDomain : `https://${tenantDomain}`;
          console.log('收到其他页签的租户切换通知，准备跳转到:', url);
          window.location.href = url;
        } else {
          // 没有配置域名，刷新当前页面
          console.log('收到其他页签的租户切换通知，准备刷新页面');
          window.location.reload();
        }
      }
    }
  });
};

// 应用挂载后初始化跨页签监听
setupCrossTabListener();
